/** TerrainExporter — collects MapObjects relevant to a terrain-height problem area for pasting
 * into a unit-test fixture. Sets merged (dedup by id): regions intersecting the export square;
 * their referenced definitions; objects with a `terrainFeature`-bearing ObjPath in the square. */

import {ResolveObjPosInWorld} from "../../Server/DB/mapObjects/$mapObject.js";
import {MapObject} from "../../Server/DB/mapObjects/@MapObject.js";
import {ObjPath} from "../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {Rect, Vec2} from "../../Utils/ThreeJS/VectorStructs.js";
import {PathTerrainFeature} from "./IsolineTypes.js";
import {PathSceneryFeature} from "../Scenery/@SceneryFeature.js";

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

/** Approximate world-space AABB of an object for intersection testing. */
export function ApproximateObjWorldRect(obj: MapObject): Rect {
	const objPos = ResolveObjPosInWorld(obj);
	// Start from a zero-size rect at the world position, then grow it to cover the local extents.
	const rect = new Rect(objPos.x, objPos.y, 0, 0, true);
	if (obj.shape_size) {
		rect.Encapsulate(Rect.FromCenter(objPos.ToVec2(), new Vec2(obj.shape_size.x, obj.shape_size.y)));
	}
	for (const mesh of obj.meshes ?? []) {
		for (const pt of mesh.points) {
			rect.Encapsulate(new Rect(objPos.x + pt[0], objPos.y + pt[1], 0, 0, true));
		}
	}
	for (const path of obj.paths ?? []) {
		for (const pt of path.points) {
			rect.Encapsulate(new Rect(objPos.x + pt[0], objPos.y + pt[1], 0, 0, true));
		}
	}
	return rect;
}

/** Returns true if the object's AABB overlaps the axis-aligned export square.
 *  @param halfSide Half the side-length of the export square (centred at `center`). */
/*function aabbOverlaps(
	objPos: {x: number, y: number},
	extents: {halfX: number, halfY: number},
	center: {x: number, y: number},
	halfSide: number,
): boolean {
	return Math.abs(objPos.x - center.x) <= halfSide + extents.halfX
		&& Math.abs(objPos.y - center.y) <= halfSide + extents.halfY;
}*/

/** Centroid of a path's control points in LOCAL object-space. */
export function PathLocalCentroid(path: ObjPath): {x: number, y: number} {
	if (!path.points?.length) return {x: 0, y: 0};
	let sx = 0, sy = 0;
	for (const pt of path.points) { sx += pt[0]; sy += pt[1]; }
	return {x: sx / path.points.length, y: sy / path.points.length};
}

/** World-space centroid of a path — adds object world-position to local centroid. */
export function PathWorldCentroid(path: ObjPath, obj: MapObject): {x: number, y: number} {
	const local = PathLocalCentroid(path);
	const objPos = ResolveObjPosInWorld(obj);
	return {x: (objPos?.x ?? 0) + local.x, y: (objPos?.y ?? 0) + local.y};
}

// ---------------------------------------------------------------------------
// Core export function
// ---------------------------------------------------------------------------

export interface TerrainExportResult {
	/** De-duplicated list of terrain-relevant objects, in collection order. */
	objects: MapObject[];
	/** Resolved world positions keyed by object id (computed at export time, before ancestor tree is lost). */
	worldPositions: Map<string, {x: number, y: number, z: number}>;
	/** Human-readable labels for why each object was included. */
	reasons: Map<string, string[]>;
}

/** Options for {@link CollectTerrainRelevantObjects}. */
export interface CollectTerrainRelevantObjectsOptions {
	/** Axis-aligned world rect used as the area filter. When omitted, the area check is skipped
	 *  entirely (every object on the map is eligible area-wise) — used by the outliner `for:terrain`
	 *  filter, which collects terrain-relevant objects regardless of position. */
	inclusionRect?: Rect | null;
	/** When true, disabled terrain elements still match (a disabled TerrainRegion/
	 *  TerrainDefinition modifier, or a path with an inactive terrainFeature). Used by the outliner
	 *  `for:terrain` filter. Default false (the export dialog's stricter behaviour). */
	includeDisabledTerrainElements?: boolean;
}

export function CollectTerrainRelevantObjects(
	objectsToSearch: MapObject[],
	opts: CollectTerrainRelevantObjectsOptions = {},
): TerrainExportResult {
	const {inclusionRect = null, includeDisabledTerrainElements = false} = opts;
	const included = new Map<string, MapObject>();
	const worldPositions = new Map<string, {x: number, y: number, z: number}>();
	const reasons = new Map<string, string[]>();

	const addReason = (id: string, reason: string)=>{
		if (!reasons.has(id)) reasons.set(id, []);
		reasons.get(id)!.push(reason);
	};

	const include = (obj: MapObject, reason: string)=>{
		if (!included.has(obj.id)) {
			included.set(obj.id, obj);
			const p = ResolveObjPosInWorld(obj);
			if (p) worldPositions.set(obj.id, {x: p.x, y: p.y, z: p.z ?? 0});
		}
		addReason(obj.id, reason);
	};

	/** Area gate: passes when no rect was supplied (whole-map mode) or the object's AABB
	 *  overlaps the supplied rect (export-area mode). */
	const withinArea = (obj: MapObject): boolean=>inclusionRect == null || inclusionRect.Intersects(ApproximateObjWorldRect(obj));

	// Whether a modifier counts as present, honouring `includeDisabledTerrainElements`.
	const modActive = (m: {enabled?: boolean}): boolean=>includeDisabledTerrainElements || !!m.enabled;

	// -----------------------------------------------------------------------
	// Step 1: TerrainRegion objects whose AABB intersects the export area (or any, when no rect)
	// -----------------------------------------------------------------------
	const regionTerrainIDs = new Set<string>();
	for (const obj of objectsToSearch) {
		const hasRegion = (obj.modifiers ?? []).some(m=>modActive(m) && m.terrainRegion != null);
		if (!hasRegion) continue;

		if (withinArea(obj)) {
			include(obj, "TerrainRegion (intersects export area)");
			for (const mod of obj.modifiers ?? []) {
				if (modActive(mod) && mod.terrainRegion?.terrainID) {
					regionTerrainIDs.add(mod.terrainRegion.terrainID);
				}
			}
		}
	}

	// -----------------------------------------------------------------------
	// Step 2: TerrainDefinition objects whose terrainID is used by any region
	// -----------------------------------------------------------------------
	for (const obj of objectsToSearch) {
		const hasDef = (obj.modifiers ?? []).some(m=>modActive(m) && m.terrainDefinition != null);
		if (!hasDef) continue;
		for (const mod of obj.modifiers ?? []) {
			if (modActive(mod) && mod.terrainDefinition) {
				const tid = mod.terrainDefinition.terrainID;
				if (regionTerrainIDs.has(tid)) {
					include(obj, `TerrainDefinition (terrainID="${tid}" matches a region)`);
					break;
				}
			}
		}
	}

	// -----------------------------------------------------------------------
	// Step 3: Objects with V3 terrainFeature-bearing paths in the export area
	// -----------------------------------------------------------------------
	for (const obj of objectsToSearch) {
		const terrainPaths = (obj.paths ?? []).filter(p=>(includeDisabledTerrainElements && p.terrainFeature != null) || PathTerrainFeature.IsActive(p.terrainFeature));
		if (!terrainPaths.length) continue;
		if (withinArea(obj)) {
			include(obj, `has terrainFeature path (AABB intersects export square)`);
		}
	}

	return {objects: [...included.values()], worldPositions, reasons};
}

// ---------------------------------------------------------------------------
// Material-context collector
// ---------------------------------------------------------------------------

/** Options for {@link CollectMaterialContextRelevantObjects}. */
export interface CollectMaterialContextRelevantObjectsOptions {
	/** Axis-aligned world rect used as the area filter. When omitted, the area check is skipped
	 *  entirely (every object on the map is eligible area-wise) — used by the outliner `for:material`
	 *  filter. */
	inclusionRect?: Rect | null;
	/** When true, disabled material elements still match (a disabled `Mod_TerrainMaterials` modifier,
	 *  or a path with a disabled/inert `materialFeature`). Used by the outliner `for:material` filter.
	 *  Default false (the export dialog's stricter behaviour). */
	includeDisabledMaterialElements?: boolean;
}

/** Collect MapObjects relevant to a procedural-material problem area (dedup by id): objects
 *  carrying a `Mod_TerrainMaterials` modifier, or a `materialFeature`-bearing path, intersecting
 *  the export square. `for:material` uses this whole-map + disabled-inclusive — length 0/1 = match. */
export function CollectMaterialContextRelevantObjects(
	objectsToSearch: MapObject[],
	opts: CollectMaterialContextRelevantObjectsOptions = {},
): TerrainExportResult {
	const {inclusionRect = null, includeDisabledMaterialElements = false} = opts;
	const included = new Map<string, MapObject>();
	const worldPositions = new Map<string, {x: number, y: number, z: number}>();
	const reasons = new Map<string, string[]>();

	const addReason = (id: string, reason: string)=>{
		if (!reasons.has(id)) reasons.set(id, []);
		reasons.get(id)!.push(reason);
	};

	const include = (obj: MapObject, reason: string)=>{
		if (!included.has(obj.id)) {
			included.set(obj.id, obj);
			const p = ResolveObjPosInWorld(obj);
			if (p) worldPositions.set(obj.id, {x: p.x, y: p.y, z: p.z ?? 0});
		}
		addReason(obj.id, reason);
	};

	/** Area gate: passes when no rect was supplied (whole-map mode) or the object's AABB
	 *  overlaps the supplied rect (export-area mode). */
	const withinArea = (obj: MapObject): boolean=>inclusionRect == null || inclusionRect.Intersects(ApproximateObjWorldRect(obj));

	// Whether a modifier counts as present, honouring `includeDisabledMaterialElements`.
	const modActive = (m: {enabled?: boolean}): boolean=>includeDisabledMaterialElements || !!m.enabled;

	// Whether a materialFeature counts as present, honouring `includeDisabledMaterialElements`.
	// Disabled-inclusive = struct exists; otherwise must be `PathMaterialFeature.IsActive`.
	// Structural shape typed inline (avoids a circular import — TerrainExporter is on the rasterizer path).
	const mfActive = (mf: {disabled?: boolean|n; recipeID?: string|n}|n): boolean=>{
		if (includeDisabledMaterialElements) return mf != null;
		if (mf == null) return false;
		if (mf.disabled) return false;
		return mf.recipeID != "";
	};

	// Step 1: Objects carrying a `Mod_TerrainMaterials` modifier
	for (const obj of objectsToSearch) {
		const hasMaterialsMod = (obj.modifiers ?? []).some(m=>modActive(m) && m.terrainMaterials != null);
		if (!hasMaterialsMod) continue;
		//if (withinArea(obj)) {
		include(obj, "TerrainMaterials modifier (might contribute custom materials that are used)");
	}

	// Step 2: Objects with a materialFeature-bearing path in the export area
	for (const obj of objectsToSearch) {
		const materialPaths = (obj.paths ?? []).filter(p=>mfActive(p.materialFeature));
		if (!materialPaths.length) continue;
		if (withinArea(obj)) {
			include(obj, "has materialFeature path (AABB intersects export square)");
		}
	}

	return {objects: [...included.values()], worldPositions, reasons};
}

// ---------------------------------------------------------------------------
// Scenery-context collector
// ---------------------------------------------------------------------------

/** Options for {@link CollectSceneryRelevantObjects}. */
export interface CollectSceneryRelevantObjectsOptions {
	/** Axis-aligned world rect used as the area filter. When omitted, the area check is skipped
	 *  entirely (every object on the map is eligible area-wise) — used by the outliner `for:scenery`
	 *  filter. */
	inclusionRect?: Rect | null;
	/** When true, disabled scenery elements still match (a path with a disabled/`disabled`
	 *  `sceneryFeature`). Used by the outliner `for:scenery` filter. Default false (the stricter
	 *  behaviour — only paths with an active `sceneryFeature` match). */
	includeDisabledSceneryElements?: boolean;
}

/** Collect MapObjects relevant to a scenery-placement problem area (dedup by id): objects carrying
 *  a `sceneryFeature`-bearing path, intersecting the export square. `for:scenery` uses this
 *  whole-map + disabled-inclusive — length 0/1 = match. */
export function CollectSceneryRelevantObjects(
	objectsToSearch: MapObject[],
	opts: CollectSceneryRelevantObjectsOptions = {},
): TerrainExportResult {
	const {inclusionRect = null, includeDisabledSceneryElements = false} = opts;
	const included = new Map<string, MapObject>();
	const worldPositions = new Map<string, {x: number, y: number, z: number}>();
	const reasons = new Map<string, string[]>();

	const addReason = (id: string, reason: string)=>{
		if (!reasons.has(id)) reasons.set(id, []);
		reasons.get(id)!.push(reason);
	};

	const include = (obj: MapObject, reason: string)=>{
		if (!included.has(obj.id)) {
			included.set(obj.id, obj);
			const p = ResolveObjPosInWorld(obj);
			if (p) worldPositions.set(obj.id, {x: p.x, y: p.y, z: p.z ?? 0});
		}
		addReason(obj.id, reason);
	};

	/** Area gate: passes when no rect was supplied (whole-map mode) or the object's AABB
	 *  overlaps the supplied rect (export-area mode). */
	const withinArea = (obj: MapObject): boolean=>inclusionRect == null || inclusionRect.Intersects(ApproximateObjWorldRect(obj));

	// Whether a modifier counts as present, honouring `includeDisabledMaterialElements`.
	const modActive = (m: {enabled?: boolean}): boolean=>includeDisabledSceneryElements || !!m.enabled;

	// Whether a sceneryFeature counts as present, honouring `includeDisabledSceneryElements`.
	// Disabled-inclusive = struct exists; otherwise must be `PathSceneryFeature.IsActive`.
	const sfActive = (sf: PathSceneryFeature|null): boolean=>{
		if (includeDisabledSceneryElements) return sf != null;
		return PathSceneryFeature.IsActive(sf);
	};

	// Step 1: Objects carrying a `Mod_TerrainSceneries` modifier
	for (const obj of objectsToSearch) {
		const hasSceneriesMod = (obj.modifiers ?? []).some(m=>modActive(m) && m.terrainSceneries != null);
		if (!hasSceneriesMod) continue;
		//if (withinArea(obj)) {
		include(obj, "TerrainSceneries modifier (might contribute custom scenery-kinds that are used)");
	}

	// Step 2: Objects with a sceneryFeature-bearing path in the export area
	for (const obj of objectsToSearch) {
		const sceneryPaths = (obj.paths ?? []).filter(p=>sfActive(p.sceneryFeature ?? null));
		if (!sceneryPaths.length) continue;
		if (withinArea(obj)) {
			include(obj, "has sceneryFeature path (AABB intersects export square)");
		}
	}

	return {objects: [...included.values()], worldPositions, reasons};
}